<script lang="ts">
  import { chunkArrayInGroups, timeAgo } from '$lib/js/helpers'
  import { t } from 'svelte-i18n-lingui'
  import type { ProductReview } from '$lib/js/elasticsearch/types'

  interface Props {
    product: any;
    reviews: ProductReview[];
  }

  let { product, reviews }: Props = $props();

  let currentPage = $state(1)
  let pageSize = 5

  let dividedReviews = $derived(chunkArrayInGroups(reviews, pageSize))
  let paginatedReviews = $derived(dividedReviews[currentPage - 1])


  function prevPage() {
    currentPage = currentPage !== 1 ? (currentPage - 1) : dividedReviews.length
    paginatedReviews = dividedReviews[currentPage - 1]
  }

  function nextPage() {
    currentPage = currentPage === (dividedReviews.length) ? 1 : (currentPage + 1)
    paginatedReviews = dividedReviews[currentPage - 1]
  }

  function goToPage(i: number) {
    currentPage = i
    paginatedReviews = dividedReviews[i - 1]
  }
</script>


<div id="reviews" class="product-reviews">
  <div class="product-reviews__inner">
    <div class="product-reviews__heading">
      <h2 class="head-4">{$t`Reviews about ${product.name}`}</h2>

      <div class="product-reviews__rating">
        <h3 class="lead-1">{product.rating_average.toFixed(2)} <span style="width: {parseInt(product.rating_average, 10) * 18}px"></span></h3>
        <p>{$t`Based on ${product.reviews_number} reviews`}</p>
      </div>
    </div>

    {#each paginatedReviews as rev}
      <div class="product-reviews__rev">
        <div class="product-reviews__rev__left">
          <h3 class="name lead-1">{rev.author.name}</h3>
          <p><span class="icon icon-check"></span>{$t`Verified customer`}</p>
          <div class="date">{timeAgo(rev.date_created)}</div>
        </div>

        <div class="product-reviews__rev__right">
          <div class="product-reviews__rev__stars">
            {#each { length: rev.rating } as _, i}
              <span class="icon icon-star-filled"></span>
            {/each}

            {#if rev.rating !== 5}
              {#each { length: (5 - rev.rating) } as _, i}
                <span class="icon icon-star"></span>
              {/each}
            {/if}
          </div>

          {#if rev.title}
            <h3 class="lead-1">{rev.title}</h3>
          {:else}
            <h3 class="lead-1" style="margin-top: 0">...</h3>
          {/if}

          {#if rev.comments}
            <p>{rev.comments}</p>
          {:else}
            <p style="font-style: italic">{$t`Opinion's author didn't leave any comments`}</p>
          {/if}

          {#if rev.photos}
            <div class="product-reviews__imgs">
              {#each rev.photos as photo}
                <img src="{photo}" alt="{$t`Product image`}" title="{$t`Product image`}">
              {/each}
            </div>
          {/if}
        </div>
      </div>
    {/each}

    {#if product.reviews_number > pageSize}
      <div class="pagination">
        <button onclick={() => prevPage()}><span class="icon icon-left"></span></button>

        <div class="numbers">
          {#each Array(Math.trunc(product.reviews_number / pageSize) + 1).keys() as i}
            <button onclick={() => goToPage(i + 1)} class="number number_{i}"
                    class:selected={currentPage === i + 1}>{i + 1}</button>
          {/each}
        </div>

        <button onclick={() => nextPage()}><span class="icon icon-right"></span></button>
      </div>
    {/if}
  </div>
</div>


<style lang="scss">
  .pagination {
    display: flex;
    align-items: center;
    margin: 40px 0 20px;

    button {
      &:hover {
        color: var(--main);
      }
    }

    .numbers {
      margin: 0 20px;
    }

    .number {
      padding: 6px;
      margin: 0 2px;

      &.selected {
        color: var(--main);
      }
    }
  }

  .product-reviews {
    padding: 70px 0 0;

    &__imgs {
      margin: 10px -5px 0;

      img {
        width: 120px;
        margin: 5px;
      }
    }

    &__heading {
      margin-bottom: 30px;

      @media (min-width: 769px) {
        display: flex;
        justify-content: space-between;
      }
    }

    &__rating {
      h3 {
        font-size: 30px;
        margin-bottom: 5px;

        span {
          display: inline-block;
          width: 91px;
          height: 17px;
          background-image: url('/images/icons/star.png');
          background-size: 18px;
        }
      }

      p {
        @media (min-width: 769px) {
          text-align: right;
        }
      }
    }

    &__rev {
      margin-bottom: 20px;

      @media (max-width: 1024px) {
        padding-top: 20px;
        border-top: 1px solid #d7d7d7;
      }

      @media (min-width: 769px) {
        display: flex;
        justify-content: space-between;
        margin-bottom: 40px;
      }

      h3 {
        margin-bottom: 8px;

        &.name {
          text-transform: capitalize;
        }
      }

      &__left {
        margin-bottom: 20px;

        @media (min-width: 769px) {
          padding-right: 20px;
          border-right: 1px solid #d7d7d7;
          width: 220px;
          margin-bottom: 0;
        }

        p {
          color: green;

          span {
            font-size: 16px;
            margin-right: 7px;
          }
        }

        .date {
          margin-top: 10px;
        }
      }

      &__right {
        @media (min-width: 769px) {
          width: calc(100% - 260px);
        }

        .icon {
          margin-right: 3px;
          font-size: 16px;
          color: var(--main);
        }

        h3 {
          margin-top: 10px;
        }
      }

      &__stars {
        display: flex;
      }
    }
  }
</style>
